import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { devWarn } from "@/lib/log";
import { installAdminAuthInterceptor } from "@/lib/authInterceptor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Install the axios 401 interceptor exactly once at module load — it needs to
// intercept requests fired before the AuthProvider mounts as well.
installAdminAuthInterceptor();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem("admin_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("admin_token", res.data.token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      devWarn("Logout request failed:", err.message);
    }
    localStorage.removeItem("admin_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
